/**
 * Cases: counts by status across the practice, or the client's open cases
 * with their deadlines.
 */
import { Briefcase } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { MetaLabel } from "@/components/surface";
import { cn } from "@/lib/utils";

import { dueLabel, urgencyClass } from "../../format";
import type { CaseRow, PracticeCaseCounts } from "../../types";
import { EntityLink } from "../EntityLink";
import { CardRow, DashboardCard } from "./DashboardCard";

type Common = {
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onViewAll?: () => void;
  span?: "full";
};

type PracticeProps = Common & { counts: PracticeCaseCounts | undefined };

type ClientProps = Common & {
  cases: { open: CaseRow[]; total: number } | undefined;
};

type Props =
  | (PracticeProps & { mode: "practice" })
  | (ClientProps & { mode: "client" });

const PracticeCasesCard: FC<PracticeProps> = ({
  counts,
  isLoading,
  error,
  onRetry,
  onViewAll,
  span
}) => {
  const navigate = useNavigate();
  const statuses = Object.entries(counts?.by_status ?? {});
  return (
    <DashboardCard
      icon={Briefcase}
      title="Cases"
      count={counts?.open ?? null}
      onViewAll={onViewAll}
      isLoading={isLoading && !counts}
      error={error}
      onRetry={onRetry}
      isEmpty={!counts || counts.total === 0}
      emptyTitle="No Cases Yet"
      emptyDescription="Open a case from a client's profile."
      footer={
        counts?.unassigned ? (
          <div className="border-t border-border px-3 py-1.5 text-xs text-warn">
            {counts.unassigned} unassigned
          </div>
        ) : null
      }
      span={span}
      testId="cases-card"
    >
      {statuses.map(([status, n]) => (
        <CardRow
          key={status}
          onClick={() => navigate("/cases")}
          primary={status}
          trailing={<MetaLabel className="text-fg-2">{n}</MetaLabel>}
          testId="cases-status-row"
        />
      ))}
    </DashboardCard>
  );
};

const ClientCasesCard: FC<ClientProps> = ({
  cases,
  isLoading,
  error,
  onRetry,
  onViewAll,
  span
}) => {
  const navigate = useNavigate();
  const open = cases?.open ?? [];
  return (
    <DashboardCard
      icon={Briefcase}
      title="Open Cases"
      count={open.length}
      onViewAll={onViewAll}
      isLoading={isLoading && !cases}
      error={error}
      onRetry={onRetry}
      isEmpty={open.length === 0}
      emptyTitle="No Open Cases"
      emptyDescription={cases?.total ? `${cases.total} closed.` : undefined}
      span={span}
      testId="cases-card"
    >
      {open.map((row) => (
        <CardRow
          key={row.individual_id}
          onClick={() => navigate(row.path)}
          primary={
            <EntityLink path={row.path} refMarkdown={row.ref}>
              {[row.visa_type, row.notice_label ?? (row.is_rfe ? "RFE" : null)]
                .filter(Boolean)
                .join(" ") ||
                row.applicant ||
                "Case"}
            </EntityLink>
          }
          secondary={[row.status, row.assignees?.join(", ")]
            .filter(Boolean)
            .join(" · ")}
          trailing={
            row.filing_deadline ? (
              <span
                className={cn(
                  "text-xs tabular-nums",
                  urgencyClass(row.days_left, row.overdue)
                )}
              >
                {dueLabel(row.filing_deadline, { allDay: true })}
              </span>
            ) : null
          }
          testId="case-row"
        />
      ))}
    </DashboardCard>
  );
};

export const CasesCard: FC<Props> = (props) => {
  const { mode } = props;
  return mode === "practice" ? (
    <PracticeCasesCard {...(props as PracticeProps)} />
  ) : (
    <ClientCasesCard {...(props as ClientProps)} />
  );
};
